import { useMemo } from "react";
import { CheckCompany, CheckTaxParameter, CheckResponseError, Workspace } from "winslow-core";
import { Button, Label } from "../../../ds2/components";
import { apiCheckGetCompany } from "../../../services/PayrollService";

type AppPayTaxParameterSetupProps = {
    checkCompany: CheckCompany | null;
    checkTaxParameters: CheckTaxParameter[] | null;
    loadingTaxParameters: boolean;
    workspace: Workspace | null;
    onEditTaxParameter: (taxParameter: CheckTaxParameter) => void;
    onCheckCompanyUpdated: (checkCompany: CheckCompany) => void;
    onError?: (error: CheckResponseError | undefined) => void;
};

export const AppPayTaxParameterSetup = ({
    checkCompany,
    checkTaxParameters,
    loadingTaxParameters,
    workspace,
    onEditTaxParameter,
    onCheckCompanyUpdated,
    onError,
}: AppPayTaxParameterSetupProps) => {
    // Filter tax parameters to only show those matching setup_parameters jurisdiction IDs
    const filteredTaxParameters = useMemo(() => {
        if (!checkTaxParameters || !checkCompany?.onboard?.setup_parameters) {
            return checkTaxParameters || [];
        }
        const setupParameterIds = checkCompany.onboard.setup_parameters.map(issue => issue.id);
        return checkTaxParameters.filter(param => setupParameterIds.includes(param.jurisdiction));
    }, [checkTaxParameters, checkCompany?.onboard?.setup_parameters]);

    const refreshCheckCompany = async () => {
        if (workspace?.company?.checkCompanyId) {
            const apiResponseCompany = await apiCheckGetCompany(workspace.company.checkCompanyId);
            if (apiResponseCompany?.data) {
                onCheckCompanyUpdated(apiResponseCompany.data);
            }
        }
    };

    return (
        <div className="app-pay-enroll-new-contents">
            <div className="app-pay-enroll-new-left">
                <Label>
                    Tax Parameters
                </Label>
                {loadingTaxParameters ? (
                    <div style={{ marginTop: "16px" }}>
                        Loading tax parameters...
                    </div>
                ) : filteredTaxParameters.length === 0 ? (
                    <div style={{ marginTop: "16px" }}>
                        No tax parameters found.
                    </div>
                ) : (
                    <div style={{ marginTop: "16px" }}>
                        <div className="app-pay-bank-accounts-table-container">
                            <table className="app-pay-bank-accounts-table">
                                <thead>
                                    <tr>
                                        <th>Parameter</th>
                                        <th style={{ minWidth: "150px", whiteSpace: "nowrap" }}>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTaxParameters.map((param) => {
                                        const status = param.setting?.status || "needs_attention";
                                        const isCompleted = status === "completed";
                                        const hasSetting = param.setting !== null && param.setting !== undefined;
                                        const settingValue = param.setting?.value;
                                        return (
                                            <tr key={param.id} className="app-pay-bank-accounts-row">
                                                <td>
                                                    <div style={{ fontWeight: "500" }}>{param.label}</div>
                                                    {param.description && (
                                                        <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                                            {param.description}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ minWidth: "150px", whiteSpace: "nowrap" }}>
                                                    {hasSetting && settingValue !== null && settingValue !== undefined ? (
                                                        <div style={{ fontSize: "14px", color: "#333" }}>
                                                            {String(settingValue)}
                                                        </div>
                                                    ) : (
                                                        <span style={{
                                                            padding: "4px 8px",
                                                            borderRadius: "4px",
                                                            fontSize: "12px",
                                                            fontWeight: "500",
                                                            backgroundColor: "#fef3c7",
                                                            color: "#92400e"
                                                        }}>
                                                            Needs Attention
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <Button
                                                        label="Edit"
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => {
                                                            onEditTaxParameter(param);
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

